import { apiFetch } from './client';
import { API_BASE, authHeader as sessionHeaders } from '../../api/client';
import type { Conversation, ChatMessage } from '../types/contracts';

export function listConversations(archived = false): Promise<Conversation[]> {
  return apiFetch<Conversation[]>(
    `/api/conversations?archived=${archived ? 'true' : 'false'}`,
  );
}

export function createConversation(title?: string): Promise<Conversation> {
  return apiFetch<Conversation>('/api/conversations', {
    method: 'POST',
    body: JSON.stringify({ title }),
  });
}

export function getConversation(id: string): Promise<Conversation> {
  return apiFetch<Conversation>(`/api/conversations/${id}`);
}

/** P1.2: выбор агента для беседы. null = оркестратор (по умолчанию). */
export function setConversationAgent(id: string, agentId: string | null): Promise<Conversation> {
  return apiFetch<Conversation>(`/api/conversations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ agentId }),
  });
}

export function renameConversation(id: string, title: string): Promise<Conversation> {
  return apiFetch<Conversation>(`/api/conversations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  });
}

export function setArchived(id: string, archived: boolean): Promise<Conversation> {
  return apiFetch<Conversation>(`/api/conversations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ archived }),
  });
}

export function deleteConversation(id: string): Promise<void> {
  return apiFetch<void>(`/api/conversations/${id}`, { method: 'DELETE' });
}

export function listMessages(id: string): Promise<ChatMessage[]> {
  return apiFetch<ChatMessage[]>(`/api/conversations/${id}/messages`);
}

export interface SendResult {
  userMessage: ChatMessage;
  assistantMessage: ChatMessage;
}

/** Синхронный (legacy) */
export function sendMessage(
  id: string,
  text: string,
  fileIds?: string[],
): Promise<SendResult> {
  return apiFetch<SendResult>(`/api/conversations/${id}/messages`, {
    method: 'POST',
    body: JSON.stringify({ text, fileIds }),
  });
}

// ─── SSE стриминг ──────────────────────────────────────────────

// Набор событий строго соответствует тому, что реально шлёт бэкенд
// (apps/api/src/routes/conversations.ts): user_message, delta, status,
// usage, done, error. Мёртвых событий (token, content, conversation_created)
// в протоколе нет.
export type SseEvent =
  | { type: 'user_message'; message: ChatMessage }
  | { type: 'delta'; text: string }
  | { type: 'status'; text: string }
  | { type: 'automation_suggestion'; prompt: string }
  // Плавающий чат-навигатор: бэкенд просит перейти на раздел сайта.
  | { type: 'navigate'; path: string; label: string }
  | { type: 'usage'; promptTokens: number; completionTokens: number; costUsd: number }
  | { type: 'done'; messageId: string }
  | { type: 'error'; message: string }
  // Предупреждение о приближении к лимиту токенов (~80%), один раз за период.
  | { type: 'warning'; text: string }
  | { type: 'heartbeat' }
  // Агентская лента (Фича 1)
  | { type: 'message_delta'; step: number; text: string; isFinal: boolean }
  | { type: 'tool_call'; step: number; tool: string; args?: Record<string, unknown> }
  | { type: 'tool_result'; step: number; tool: string; ok: boolean; result?: string; truncated?: boolean }
  // Файл-артефакт (Фича 2)
  | { type: 'file'; name: string; url: string; fileId?: string };

// Настройки стриминга
export interface StreamingPrefs {
  show_reasoning: boolean;
  show_tool_details: boolean;
  stream_intermediate: boolean;
}

export function getStreamingPrefs(): Promise<StreamingPrefs> {
  return apiFetch<StreamingPrefs>('/api/conversations/streaming-prefs');
}

export function updateStreamingPrefs(prefs: Partial<StreamingPrefs>): Promise<{ ok: boolean } & Partial<StreamingPrefs>> {
  return apiFetch<{ ok: boolean } & Partial<StreamingPrefs>>('/api/conversations/streaming-prefs', {
    method: 'PATCH',
    body: JSON.stringify(prefs),
  });
}

/** Ошибка запроса стрима с HTTP-статусом (нужно, чтобы отличать лимит 429). */
export interface StreamError extends Error {
  status?: number;
}

export interface StreamMessageOptions {
  conversationId: string;
  text: string;
  fileIds?: string[];
  /** Какие бэкенд-возможности клиент готов отрабатывать. Only 'navigate' сейчас используется. */
  clientCapabilities?: string[];
  onEvent: (event: SseEvent) => void;
  /** Вызывается при любой ошибке */
  onError?: (err: StreamError) => void;
  signal?: AbortSignal;
}

/**
 * Отправляет сообщение через SSE-стриминг.
 * Использует fetch + ReadableStream для SSE парсинга.
 */
export async function streamMessage(opts: StreamMessageOptions): Promise<void> {
  const { conversationId, text, fileIds, clientCapabilities, onEvent, onError, signal } = opts;
  const apiBase = API_BASE;

  const timeoutMs = 90000;
  const internalAbort = new AbortController();
  const onCallerAbort = () => internalAbort.abort();
  if (signal) {
    signal.addEventListener('abort', onCallerAbort);
    if (signal.aborted) internalAbort.abort();
  }

  let watchdog: ReturnType<typeof setTimeout> | undefined = undefined;
  const resetWatchdog = () => {
    clearTimeout(watchdog);
    watchdog = setTimeout(() => {
      internalAbort.abort(new Error(`Ответ сервера не получен в течение ${timeoutMs / 1000}с (Таймаут)`));
    }, timeoutMs);
  };

  resetWatchdog();

  let res: Response;
  try {
    res = await fetch(`${apiBase}/api/conversations/${conversationId}/messages/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...sessionHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify({ text, fileIds, clientCapabilities }),
      signal: internalAbort.signal,
    });
  } catch (err) {
    onError?.(err instanceof Error ? err : new Error(String(err)));
    return;
  }

  if (!res.ok || !res.body) {
    // Пытаемся достать текст ошибки с сервера (например, сообщение о лимите 429).
    let serverMsg = '';
    try {
      const data = await res.json();
      if (data && typeof data.error === 'string') serverMsg = data.error;
    } catch { /* тело не JSON — используем статус */ }
    const err: StreamError = new Error(serverMsg || `HTTP ${res.status}`);
    err.status = res.status;
    onError?.(err);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      resetWatchdog();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      // Парсим SSE-линии
      const lines = buf.split('\n');
      buf = lines.pop() ?? '';
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const json = line.slice(6).trim();
          if (!json) continue;
          try {
            const event = JSON.parse(json) as SseEvent;
            if (event.type !== 'heartbeat') {
              onEvent(event);
            }
          } catch { /* ignore bad JSON */ }
        }
      }
    }
  } catch (err) {
    if ((err as Error).name !== 'AbortError' && !internalAbort.signal.aborted) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } else if (internalAbort.signal.aborted && internalAbort.signal.reason) {
      onError?.(internalAbort.signal.reason as Error);
    }
  } finally {
    clearTimeout(watchdog);
    if (signal) signal.removeEventListener('abort', onCallerAbort);
    reader.releaseLock();
  }
}

// ─── Файлы ─────────────────────────────────────────────────────

export interface FileUploadResult {
  fileId: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  downloadUrl: string;
}

/**
 * Загружает файл через base64-JSON.
 */
export async function uploadFile(
  conversationId: string,
  file: File,
): Promise<FileUploadResult> {
  const data = await fileToBase64(file);
  return apiFetch<FileUploadResult>(`/api/conversations/${conversationId}/files`, {
    method: 'POST',
    body: JSON.stringify({ name: file.name, mimeType: file.type, data }),
  });
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // data:...;base64,XXXX  →  XXXX
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// ─── Голос ──────────────────────────────────────────────────────

export interface VoiceTranscribeResult {
  text: string;
}

/**
 * Отправляет аудио base64 на STT-транскрибацию.
 */
export async function transcribeVoice(
  conversationId: string,
  audioBlob: Blob,
): Promise<VoiceTranscribeResult> {
  const data = await blobToBase64(audioBlob);
  return apiFetch<VoiceTranscribeResult>(`/api/conversations/${conversationId}/voice`, {
    method: 'POST',
    body: JSON.stringify({ data, mimeType: audioBlob.type || 'audio/webm' }),
  });
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

// ─── Статистика токенов ────────────────────────────────────────

export interface UsageStats {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costUsd: number;
}

export function getUsage(conversationId: string): Promise<UsageStats> {
  return apiFetch<UsageStats>(`/api/conversations/${conversationId}/usage`);
}
